import React, { useEffect, useState } from 'react';
import { FormInput, InputGroupAddon, InputGroupText } from 'shards-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import getSteelCategoryQuery from '../../../utils/mill-sheet-search/getSteelCategoryQuery';
import { LANGUAGE, STEEL_CATEGORY } from '../../../constants';
import type { Language, SteelCategory } from '../../../types/common';
import type { OptionsType } from '../../../types/options';

interface QueryInputProps {
  language: Language;
  selectedSteelCategory: SteelCategory;
  steelCategoryType: OptionsType<SteelCategory, string>[];
  onChangeQuery: (value: string) => void;
}

const QueryInput = ({ language, selectedSteelCategory, steelCategoryType, onChangeQuery }: QueryInputProps): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  // コンポーネント内で管理するURLクエリ
  const [queryByUrl, setQueryByUrl] = useState<string>('');
  // フリー文字列クエリ
  const [query, setQuery] = useState<string>('');

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  // URLが変わったらクエリを分析してセットする
  // ----------------------------------------
  useEffect(() => {
    // console.log(location.search);
    const params: URLSearchParams = new URLSearchParams(location.search);
    const q: string | null = params.get('q');
    if (q !== null) {
      const queryString: string = decodeURIComponent(q);
      // 区分（Language:XX）は文字列クエリに表示しないので取り除く
      const queryStringList: string[] = queryString.split(' AND ');
      const filteredQueryStringList: string[] = queryStringList.filter((query) => query !== 'Language:JP' && query !== 'Language:EN');
      const filteredQueryString: string = filteredQueryStringList.join(' AND ');
      // console.log(filteredQueryString);
      setQueryByUrl(filteredQueryString);
    }
  }, [location.search]);

  // URLクエリが変わったら文字列クエリにセットする
  // ----------------------------------------
  useEffect(() => {
    // console.log(queryByUrl);
    setQuery(queryByUrl);
  }, [queryByUrl]);

  // フリー文字列クエリが変わったらハンドラを実行する
  // ----------------------------------------
  useEffect(() => {
    // console.log(query);
    onChangeQuery(query);
  }, [query]);

  // 分類のプルダウンが変わったらそれに応じてクエリを変更する
  // ----------------------------------------
  useEffect(() => {
    // console.log(queryByUrl, selectedSteelCategory, steelCategoryType);
    if (steelCategoryType.length > 0) {
      // クエリの準備
      const barWireRod = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.BAR)?.children ?? [];
      const sheetSteel = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.PLATE)?.children ?? [];
      const pipe = steelCategoryType.find((option) => option.code === STEEL_CATEGORY.PIPE)?.children ?? [];
      const queryBarWireRodJa: string = '品種:(' + barWireRod.map((child) => child.nameJapanese).join(' OR ') + ')';
      const querySheetSteelJa: string = '品種:(' + sheetSteel.map((child) => child.nameJapanese).join(' OR ') + ')';
      const queryPipeJa: string = '品種:(' + pipe.map((child) => child.nameJapanese).join(' OR ') + ')';
      const queryBarWireRodEn: string = '品種_EN:(' + barWireRod.map((child) => child.nameEnglish).join(' OR ') + ')';
      const querySheetSteelEn: string = '品種_EN:(' + sheetSteel.map((child) => child.nameEnglish).join(' OR ') + ')';
      const queryPipeEn: string = '品種_EN:(' + pipe.map((child) => child.nameEnglish).join(' OR ') + ')';

      // 選択された選択肢から品種のクエリを作成
      // 条件に当てはまる場合はクエリを、当てはまらない場合（分類選択をやめた場合）は空文字列を返す
      const steelCategoryQuery: string = getSteelCategoryQuery(selectedSteelCategory, steelCategoryType, language);
      // steelCategoryQueryに応じてクエリを変更する
      const queryStringList: string[] = queryByUrl !== '' ? queryByUrl.split(' AND ') : [];
      const filteredQueryStringList: string[] = queryStringList.filter((item) => {
        // console.log(item);
        if (language === LANGUAGE.JA) {
          // 日本語クエリの場合:
          // 分類の指定があったら取り除く。
          const isCategoryQuery: boolean = item === queryBarWireRodJa || item === querySheetSteelJa || item === queryPipeJa;
          return !isCategoryQuery;
        } else if (language === LANGUAGE.EN) {
          // 英語クエリの場合:
          // 分類の指定があったら取り除く。
          const isCategoryQuery: boolean = item === queryBarWireRodEn || item === querySheetSteelEn || item === queryPipeEn;
          return !isCategoryQuery;
        } else {
          return true;
        }
      });
      // console.log(queryStringList, filteredQueryStringList, steelCategoryQuery);
      // 「steelCategoryQuery === ''」の場合、つまり分類プルダウンを空欄にした場合は文字列クエリから品種クエリを削除する。
      // それ以外の場合（分類プルダウンで空欄以外を選んだ場合）は品種クエリを変更する。
      const newQueryStringList: string[] = steelCategoryQuery === '' ? filteredQueryStringList : [...filteredQueryStringList, steelCategoryQuery];
      const newQueryString: string = newQueryStringList.join(' AND ');
      // console.log(newQueryString);
      setQuery(newQueryString);
    }
  }, [selectedSteelCategory, steelCategoryType, queryByUrl]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * クエリ文字列を変更したとき
   */
  const handleQueryChange = (event: React.ChangeEvent<{ value: string }>): void => {
    const newValue: string = event.target.value;
    setQuery(newValue);
  };

  /**
   * クエリ文字列をクリアしたとき
   */
  const handleClearQuery = (): void => {
    setQuery('');
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <InputGroupAddon type="prepend">
        <InputGroupText>
          <i id="mainSearchIcon" className="material-icons">
            search
          </i>
        </InputGroupText>
      </InputGroupAddon>
      <FormInput placeholder={t('filter.default')} id="query" value={query} onChange={handleQueryChange} autoFocus="autoFocus" />
      <InputGroupAddon type="append" className="button-clear" style={{ zIndex: 2 }}>
        <InputGroupText>
          <i className="material-icons" onClick={handleClearQuery}>
            cancel
          </i>
        </InputGroupText>
      </InputGroupAddon>
    </>
  );
};

export default QueryInput;
